import React from "react";
import { MdPeopleOutline } from "react-icons/md";
import Card from "../../../../components/card";
import { PessoaListaItem } from "../../../../interfaces/Identidade";

interface Props {
  itens: PessoaListaItem[];
  busca: string;
  loading: boolean;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

const TH =
  "px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300";

/** Lista de pessoas. A linha inteira abre o painel lateral (teclado: Enter/Espaço). */
const PessoasTable: React.FC<Props> = ({ itens, busca, loading, selectedId, onSelect }) => {
  if (loading) {
    return (
      <Card extra="w-full p-6">
        <div className="space-y-3" aria-busy="true" aria-label="Carregando pessoas">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
          ))}
        </div>
      </Card>
    );
  }

  if (itens.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 p-10 text-center dark:border-white/15">
        <MdPeopleOutline
          className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500"
          aria-hidden="true"
        />
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {busca.trim()
            ? `Nenhuma pessoa encontrada para «${busca.trim()}»`
            : "Nenhuma pessoa cadastrada ainda. Elas aparecem aqui depois do backfill da identidade."}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white shadow-dv-md dark:border-white/10 dark:bg-navy-800 dark:shadow-none">
      <table className="w-full min-w-[720px] border-collapse">
        <thead className="bg-gray-50 dark:bg-navy-900">
          <tr>
            <th className={TH}>Nome</th>
            <th className={TH}>E-mail</th>
            <th className={TH}>Interna</th>
            <th className={TH}>Logins</th>
            <th className={TH}>Organizações</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((p) => (
            <tr
              key={p.id}
              tabIndex={0}
              aria-selected={selectedId === p.id}
              onClick={() => onSelect(p.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(p.id);
                }
              }}
              className={`cursor-pointer border-b border-gray-100 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-dv-blue-500 dark:border-gray-700 dark:hover:bg-navy-700 ${
                selectedId === p.id ? "bg-dv-blue-500/5 dark:bg-white/5" : ""
              }`}
            >
              <td className="max-w-[220px] px-4 py-3 text-sm font-semibold text-navy-700 dark:text-white">
                <span className="block truncate" title={p.nome}>
                  {p.nome}
                </span>
              </td>
              <td className="max-w-[240px] px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                <span className="block truncate" title={p.emailPrincipal}>
                  {p.emailPrincipal}
                </span>
              </td>
              <td className="px-4 py-3">
                <span
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    p.interna
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                      : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                  }`}
                >
                  {p.interna ? "Interna" : "Externa"}
                </span>
              </td>
              <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">
                {p.logins}
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1.5">
                  {p.vinculos.length === 0 && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">Nenhuma</span>
                  )}
                  {p.vinculos.map((v) => (
                    <span
                      key={v.clientId}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        v.ativo
                          ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                          : "bg-gray-100 text-gray-500 line-through dark:bg-navy-700 dark:text-gray-400"
                      }`}
                      title={v.ativo ? "Vínculo ativo" : "Vínculo desativado"}
                    >
                      {v.cliente}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PessoasTable;
